import React from 'react';
import styled from 'styled-components';
import CubeIcon from '@svg/cube';

import { alpha, Stack } from '@mui/material';
import { NodeIcon, NodeId, getIcon } from '../Defaults/NodeHeader';
import { Command, flowActions } from '@store/slices';
import { NodeState } from '@components/Flow/Nodes';
import { Button, LoadingButton } from '@common/Button';
import { PlayArrowOutlined } from '@mui/icons-material';
import { colorType } from 'theme';
import { NodeExecutionHeader, WorkflowExecution } from './NodeExecutionHeader';
import { RTC } from '@components/Flow/Nodes/Trigger/InboundCall/Softphone/useRTC';
import { useDispatch } from 'react-redux';

export const NodeSidebarHeaderContainer = styled.div<{ backgroundColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.text.standard};
  position: sticky;
  top: 0;
  height: 60px;
  z-index: 100;
  font-weight: 500;
  background: ${({ theme }) => theme.background.standard};
  border-bottom: 1px solid ${({ theme }) => theme.line.light};
`;

export const NodeVersion = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.badge.background};
  color: ${({ theme }) => alpha(theme.primary, 0.8)};
  border-radius: 6px;
  font-size: 13px;
  border: 1px solid ${({ theme }) => alpha(theme.primary, 0.4)};
  font-weight: 400;
  height: 24px;
  min-width: 28px;
  padding: 0 4px;
  line-height: 0.85rem;
  font-family: 'Space Grotesk';
  user-select: none;

  &:hover {
    border: 1px solid ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
    cursor: pointer;
  }
`;

interface Props {
  command: Command;
  execution?: {
    run?: () => void;
    loading: boolean;
    startIcon?: React.ReactNode;
    customColor?: keyof colorType;
  };
  workflowExecution?: WorkflowExecution;
  openModule?: () => void;
  rtc?: RTC;
}

export const NodeSidebarHeader: React.FC<Props> = ({ command, execution, openModule, workflowExecution, rtc }) => {
  const dispatch = useDispatch();

  const { icon, background } = command.config;

  const displayFieldId = command.fields.find((field) => field.display_name)?.id;
  const displayTitle = displayFieldId ? (command.state[displayFieldId as keyof NodeState] as string) : null;
  const config = command?.config;
  const version = config?.version;

  const title = displayTitle || command.config.title;

  const openChangelog = () => {
    dispatch(flowActions.setShowNodeChangelog(command.config));
  };

  // if trigger node, show workflow run header
  if (workflowExecution)
    return <NodeExecutionHeader workflowExecution={workflowExecution} command={command} rtc={rtc} />;

  return (
    <NodeSidebarHeaderContainer backgroundColor={background || ''}>
      <Stack direction='row' width='100%' justifyContent='space-between' alignItems='center'>
        <Stack direction='row' spacing={2} alignItems='center'>
          <NodeIcon background={background}>{getIcon(icon)}</NodeIcon>
          <p title={title}>{title?.length > 24 ? `${title.substring(0, 24)}...` : title}</p>
        </Stack>
        <Stack direction='row' spacing={1} alignItems='center'>
          {execution && (
            <LoadingButton
              loading={execution.loading}
              minWidth='50px'
              onClick={execution.run}
              small
              startIcon={execution?.startIcon || <PlayArrowOutlined />}
              customColor={execution?.customColor || 'blue'}
            >
              Run
            </LoadingButton>
          )}
          {openModule && (
            <Button onClick={openModule} small startIcon={<CubeIcon height={16} />} customColor='yellow'>
              Open Module
            </Button>
          )}
          <NodeId background={background}>#{command.node.id}</NodeId>
          {version && <NodeVersion onClick={openChangelog}>v{version}</NodeVersion>}
        </Stack>
      </Stack>
    </NodeSidebarHeaderContainer>
  );
};
